import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Box, Button, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { StyledErrorText } from '@shared/components/errorText';
import CustomFormLabel from '@shared/components/forms/theme-elements/CustomFormLabel';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AnalysisField } from '@features/Admin/Campaign/utils/campaign-analysis-fields';
import {
  POST_CALL_CONDITION_OPERATORS,
  PostCallMessageConditionDraft,
  PostCallMessageConditionOperator,
  operatorNeedsValue,
} from '@features/Admin/Campaign/utils/post-call-message-mapper';

interface PostCallMessageConditionsEditorProps {
  conditions: PostCallMessageConditionDraft[];
  analysisFields: AnalysisField[];
  error?: string;
  disabled?: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, condition: PostCallMessageConditionDraft) => void;
}

const BOOLEAN_VALUES = ['true', 'false'];

const PostCallMessageConditionsEditor: React.FC<PostCallMessageConditionsEditorProps> = ({
  conditions,
  analysisFields,
  error,
  disabled = false,
  onAdd,
  onRemove,
  onChange,
}) => {
  const { t } = useTranslation();

  const conditionFields = analysisFields.filter((field) => !field.name.includes('.'));

  const suggestedValues = (fieldName: string): string[] | null => {
    const field = conditionFields.find((item) => item.name === fieldName);

    if (!field) {
      return null;
    }

    if (field.type === 'boolean') {
      return BOOLEAN_VALUES;
    }

    return field.enumValues ?? null;
  };

  const valueHint = (fieldName: string): string | undefined => {
    const field = conditionFields.find((item) => item.name === fieldName);

    if (field?.type === 'integer' || field?.type === 'number') {
      return t('campaignPage.postCallMessages.numericHint');
    }

    return undefined;
  };

  return (
    <Box sx={{ mt: '20px' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <CustomFormLabel sx={{ mt: 0, fontWeight: 500 }}>
          {t('campaignPage.postCallMessages.conditionsLabel')}
        </CustomFormLabel>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAdd}
          disabled={disabled}
          variant="outlined">
          {t('campaignPage.postCallMessages.addCondition')}
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('campaignPage.postCallMessages.conditionsHint')}
      </Typography>

      {conditions.length === 0 ? (
        <BorderedGrid sx={{ padding: '16px 20px', marginBottom: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {t('campaignPage.postCallMessages.conditionsEmpty')}
          </Typography>
        </BorderedGrid>
      ) : (
        <Stack spacing={1.5}>
          {conditions.map((condition, index) => (
            <Stack
              key={index}
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  freeSolo
                  disabled={disabled}
                  options={conditionFields.map((field) => field.name)}
                  value={condition.field}
                  inputValue={condition.field}
                  onInputChange={(_event, newValue) =>
                    onChange(index, { ...condition, field: newValue })
                  }
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                    },
                    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                      padding: '12px 14px',
                    },
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      placeholder={t('campaignPage.postCallMessages.fieldPlaceholder')}
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Box sx={{ width: { xs: '100%', md: '200px' }, flexShrink: 0 }}>
                <CustomSelect
                  value={condition.operator}
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                    onChange(index, {
                      ...condition,
                      operator: event.target.value as PostCallMessageConditionOperator,
                    })
                  }
                  fullWidth
                  disabled={disabled}
                  IconComponent={IconChevronDown}>
                  {POST_CALL_CONDITION_OPERATORS.map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {t(`campaignPage.postCallMessages.operators.${operator}`)}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {operatorNeedsValue(condition.operator) ? (
                  suggestedValues(condition.field) ? (
                    <CustomSelect
                      value={condition.value}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                        onChange(index, { ...condition, value: event.target.value as string })
                      }
                      fullWidth
                      displayEmpty
                      disabled={disabled}
                      IconComponent={IconChevronDown}>
                      <MenuItem value="" disabled>
                        {t('campaignPage.postCallMessages.valuePlaceholder')}
                      </MenuItem>
                      {suggestedValues(condition.field)?.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  ) : (
                    <CustomTextField
                      value={condition.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(index, { ...condition, value: event.target.value })
                      }
                      placeholder={t('campaignPage.postCallMessages.valuePlaceholder')}
                      helperText={valueHint(condition.field)}
                      disabled={disabled}
                      fullWidth
                    />
                  )
                ) : null}
              </Box>

              <IconButton
                size="small"
                onClick={() => onRemove(index)}
                disabled={disabled}
                sx={{ alignSelf: { xs: 'flex-end', md: 'auto' }, flexShrink: 0 }}>
                <IconTrash size={18} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      {error ? <StyledErrorText>{error}</StyledErrorText> : null}
    </Box>
  );
};

export default PostCallMessageConditionsEditor;
